import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Building2, X, Download, Search, MapPin, Star, ChevronDown, ChevronLeft, ChevronRight, Filter, CheckCircle, Clock } from 'lucide-react';
import NavigationBar from '../components/NavigationBar';
import EventNavigation from '../components/EventNavigation';
import LoadingSpinner from '../components/LoadingSpinner';
import MeetingDetailsDrawer from '../components/MeetingDetailsDrawer';
import MeetingRequestDrawer from '../components/MeetingRequestDrawer';
import useEventMeetings from '../hooks/useEventMeetings';
import api from '../services/api';
import '../components/EventHome.css';
import './EventEntityCompanies.css';

// Match a question by its stable key. The numeric legacy id is only honoured for questions that
// have no key at all (pre-2027 forms that were never backfilled), so a newer event can never match
// an unrelated question that happens to reuse a 2026 id.
const isQuestion = (question, key, legacyId) =>
    question?.question_key === key ||
    (legacyId !== undefined && !question?.question_key && question?.question_id === legacyId);

// Find the question for a given key in the directory response: key match first, legacy id second
const findQuestion = (questions, key, legacyId) =>
    questions?.find(q => q?.question_key === key) ??
    (legacyId !== undefined ? questions?.find(q => !q?.question_key && q?.question_id === legacyId) : undefined);

const findQuestionId = (questions, key, legacyId) => findQuestion(questions, key, legacyId)?.question_id;

// Events created before event-scoped registration types exposed a fixed Non-Profit (1) / Philanthropist (2)
// EventEntityId. Only these may fall back to those ids when the public event-entities lookup fails.
const LEGACY_EVENT_NAME_IDS = ['F4C2025'];

/**
 * EventNonprofits - Displays nonprofits for an event (EventEntity ID 1)
 * Card-based view showing organization details
 */
const EventNonprofits = () => {
    const { eventNameId } = useParams();
    // Resolved per event from /public/event-entities (null until loaded)
    // eslint-disable-next-line no-unused-vars
    const [eventEntityId, setEventEntityId] = useState(null);
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [tableData, setTableData] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
    const [filterView, setFilterView] = useState('main'); // 'main', 'areas', 'geo'
    const [selectedAreasOfInterest, setSelectedAreasOfInterest] = useState([]);
    const [selectedGeographicFocus, setSelectedGeographicFocus] = useState([]); // Question 84 - Geographic Focus regions
    const [selectedServiceLocations, setSelectedServiceLocations] = useState([]); // US States/Countries question
    const [filterSearchAreas, setFilterSearchAreas] = useState('');
    const [filterSearchGeo, setFilterSearchGeo] = useState('');
    const [filterSearchServiceLoc, setFilterSearchServiceLoc] = useState('');

    // Meeting details drawer state
    const [meetingDrawerOpen, setMeetingDrawerOpen] = useState(false);
    const [selectedMeetingId, setSelectedMeetingId] = useState(null);
    const [selectedMeetingCompanyName, setSelectedMeetingCompanyName] = useState(null);

    // Meeting request drawer state
    const [meetingRequestDrawerOpen, setMeetingRequestDrawerOpen] = useState(false);
    const [meetingRequestContact, setMeetingRequestContact] = useState(null);

    const filterWrapperRef = useRef(null);

    // Use event meetings hook - fetches meetings from iConnections SQL Server
    const {
        getMeetingStatusForCompany,
        refresh: refreshMeetings
    } = useEventMeetings(event?.EventId, event?.ic_event_id, { autoFetch: false });

    useEffect(() => {
        if (eventNameId) {
            localStorage.setItem('last_event', eventNameId);
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventNameId]);

    // Fetch meetings when event is loaded
    useEffect(() => {
        if (event?.EventId && event?.ic_event_id) {
            refreshMeetings();
        }
    }, [event?.EventId, event?.ic_event_id, refreshMeetings]);

    // Close filter dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (filterWrapperRef.current && !filterWrapperRef.current.contains(event.target)) {
                setFilterDropdownOpen(false);
                setFilterView('main');
            }
        };

        if (filterDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [filterDropdownOpen]);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError('');

            // Fetch event data
            const eventResponse = await api.get(`/public/events/${eventNameId}`);
            setEvent(eventResponse.data);

            // Resolve the Non-Profit event entity for this event (ids differ per event)
            let resolvedEventEntityId;
            try {
                const entitiesResponse = await api.get(`/public/event-entities/${eventNameId}`);
                const nonprofitEntity = (entitiesResponse.data || []).find(e => e.Name === 'Non-Profit');
                if (!nonprofitEntity) {
                    // This event has no Non-Profit registration type: show an empty directory rather than another event's
                    setEventEntityId(null);
                    setTableData({ questions: [], companies: [] });
                    return;
                }
                resolvedEventEntityId = nonprofitEntity.EventEntityId;
            } catch (entityErr) {
                if (!LEGACY_EVENT_NAME_IDS.includes(eventNameId)) throw entityErr;
                resolvedEventEntityId = 1;
            }
            setEventEntityId(resolvedEventEntityId);

            // Fetch companies with profile data for nonprofits
            const tableResponse = await api.get(`/event-entity-companies/${resolvedEventEntityId}/profile`);
            setTableData(tableResponse.data);
        } catch (err) {
            console.error('Error fetching data:', err);
            setError(err.response?.data?.detail || 'Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    // Helper function to find question by keywords in question_text or short_description
    const findQuestionByKeywords = (keywords) => {
        if (!tableData?.questions) return null;
        return tableData.questions.find(q => {
            const text = (q.question_text + ' ' + (q.short_description || '')).toLowerCase();
            return keywords.some(kw => text.includes(kw.toLowerCase()));
        });
    };

    // Get specific field values for a company
    const getFieldValue = (company, keywords) => {
        const question = findQuestionByKeywords(keywords);
        if (!question) return null;
        return company.answers[question.question_id];
    };

    // Get field value by stable question key (falls back to legacy 2026 question id)
    const getFieldValueByKey = (company, key, legacyId) => {
        return company.answers[findQuestionId(tableData?.questions, key, legacyId)] || null;
    };

    // First, collect all known options from hierarchical data across all companies
    const knownOptions = useMemo(() => {
        if (!tableData?.companies) return { areas: new Set(), geo: new Set() };

        const areas = new Set();
        const geo = new Set();

        tableData.companies.forEach(company => {
            // Extract from areas of interest (include subcategories with parent prefix)
            const areasValue = getFieldValue(company, ['areas of interest', 'focus area', 'cause', 'primary areas']);
            if (areasValue && typeof areasValue === 'object' && areasValue.type === 'hierarchical' && areasValue.items) {
                areasValue.items.forEach(item => {
                    areas.add(item.category);
                    if (item.subcategories) {
                        item.subcategories.forEach(sub => areas.add(`${item.category}: ${sub}`));
                    }
                });
            }

            // Extract from Service Locations (Question 67 - US States/Countries)
            const geoValue = getFieldValueByKey(company, 'service_locations', 67);
            if (geoValue && typeof geoValue === 'object' && geoValue.type === 'hierarchical' && geoValue.items) {
                geoValue.items.forEach(item => geo.add(item.category));
            }
        });

        return { areas, geo };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableData?.companies]);

    // Clean up geographic names by removing extra suffixes
    const cleanGeographicName = (name) => {
        if (!name) return name;
        return name
            .replace(/, United Republic of$/i, '')
            .replace(/, State of$/i, '')
            .replace(/, Republic of$/i, '')
            .replace(/, Kingdom of$/i, '')
            .replace(/, Federation of$/i, '')
            .replace(/, Democratic Republic of$/i, '')
            .replace(/, People's Republic of$/i, '')
            .trim();
    };

    // Extract items from hierarchical value or return as array
    // Uses known options to properly split comma-separated strings
    // isGeographic flag indicates if this is geographic data (safe to split by comma)
    // includeSubcategories flag indicates if subcategories should be included
    const extractTags = (value, optionSet = null, isGeographic = false, includeSubcategories = false) => {
        if (!value) return [];
        if (typeof value === 'object' && value.type === 'hierarchical' && value.items) {
            if (includeSubcategories) {
                // Return structured data with categories and subcategories
                return value.items.map(item => ({
                    category: isGeographic ? cleanGeographicName(item.category) : item.category,
                    subcategories: item.subcategories || []
                }));
            }
            return value.items.map(item => isGeographic ? cleanGeographicName(item.category) : item.category);
        }
        if (typeof value === 'string') {
            const trimmed = value.trim();

            // For geographic data (states/countries), clean up first then split
            if (isGeographic) {
                // First clean up country names with suffixes BEFORE splitting
                const cleaned = trimmed
                    .replace(/, United Republic of/gi, '')
                    .replace(/, State of/gi, '')
                    .replace(/, Republic of/gi, '')
                    .replace(/, Kingdom of/gi, '')
                    .replace(/, Federation of/gi, '')
                    .replace(/, Democratic Republic of the/gi, '')
                    .replace(/, Democratic Republic of/gi, '')
                    .replace(/, People's Republic of/gi, '');

                return cleaned.split(',').map(s => s.trim()).filter(Boolean);
            }

            // If we have known options, try to match against them
            if (optionSet && optionSet.size > 0) {
                const matched = [];
                let remaining = trimmed;

                // Sort options by length (longest first) to match longer options first
                const sortedOptions = Array.from(optionSet).sort((a, b) => b.length - a.length);

                for (const option of sortedOptions) {
                    if (remaining.includes(option)) {
                        matched.push(option);
                        // Remove the matched option and any surrounding comma/space
                        remaining = remaining.replace(option, '|||MATCHED|||');
                    }
                }

                if (matched.length > 0) {
                    if (includeSubcategories) {
                        return matched.map(m => ({ category: m, subcategories: [] }));
                    }
                    return matched;
                }
            }

            // Fallback: return as single item
            if (includeSubcategories) {
                return [{ category: trimmed, subcategories: [] }];
            }
            return [trimmed];
        }
        if (Array.isArray(value)) {
            if (includeSubcategories) {
                return value.map(v => ({ category: v, subcategories: [] }));
            }
            return value;
        }
        return [];
    };

    // Extract flat list of all tags (categories + subcategories) for filtering
    // Subcategories include parent category prefix like "Education: Youth Development/Mentoring"
    const extractAllFilterTags = (value, optionSet = null) => {
        const structured = extractTags(value, optionSet, false, true);
        const allTags = [];
        structured.forEach(item => {
            if (typeof item === 'object' && item.category) {
                allTags.push(item.category);
                if (item.subcategories) {
                    item.subcategories.forEach(sub => allTags.push(`${item.category}: ${sub}`));
                }
            } else {
                allTags.push(item);
            }
        });
        return allTags;
    };

    // Extract distinct values for filters
    const { distinctAreasOfInterest, distinctGeographicFocus, distinctServiceLocations } = useMemo(() => {
        if (!tableData?.companies) return { distinctAreasOfInterest: [], distinctGeographicFocus: [], distinctServiceLocations: [] };

        const areasSet = new Set();
        const geoFocusSet = new Set(); // Question 84 - Geographic Focus
        const serviceLocSet = new Set(); // US States/Countries question

        tableData.companies.forEach(company => {
            // Use extractAllFilterTags for areas to include subcategories
            const areas = extractAllFilterTags(getFieldValue(company, ['areas of interest', 'focus area', 'cause', 'primary areas']), knownOptions.areas);
            const serviceLoc = extractTags(getFieldValueByKey(company, 'service_locations', 67), knownOptions.geo, true);
            
            // Question 84 - Geographic Focus (multiselect with predefined regions)
            const geoFocusValue = getFieldValueByKey(company, 'geographic_focus', 84);
            if (geoFocusValue) {
                // Split comma-separated values into individual tags
                const geoFocusTags = extractTags(geoFocusValue, null, true, false);
                geoFocusTags.forEach(g => geoFocusSet.add(g));
            }

            areas.forEach(a => areasSet.add(a));
            serviceLoc.forEach(s => serviceLocSet.add(s));
        });

        // Sort Service Locations with "United States" at top, then alphabetical
        const sortedServiceLocations = Array.from(serviceLocSet).sort((a, b) => {
            if (a === 'United States') return -1;
            if (b === 'United States') return 1;
            return a.localeCompare(b);
        });
        
        return {
            distinctAreasOfInterest: Array.from(areasSet).sort(),
            distinctGeographicFocus: Array.from(geoFocusSet).sort(),
            distinctServiceLocations: sortedServiceLocations
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableData?.companies, knownOptions]);

    // Filter companies based on search query and selected filters, then sort by company name
    const filteredCompanies = useMemo(() => {
        if (!tableData?.companies) return [];

        return tableData.companies.filter(company => {
            // Search filter
            if (searchQuery.trim()) {
                const query = searchQuery.toLowerCase();
                const matchesSearch =
                    company.CompanyName?.toLowerCase().includes(query) ||
                    Object.values(company.answers || {}).some(answer => {
                        if (typeof answer === 'string' && answer.toLowerCase().includes(query)) return true;
                        if (typeof answer === 'object' && answer?.type === 'hierarchical' && answer.items) {
                            return answer.items.some(item => item.category?.toLowerCase().includes(query));
                        }
                        return false;
                    }) ||
                    // Search through organization contacts
                    (company.contacts || []).some(contact => {
                        const fullName = `${contact.FirstName || ''} ${contact.LastName || ''}`.toLowerCase();
                        const title = (contact.Title || '').toLowerCase();
                        return fullName.includes(query) || title.includes(query);
                    });
                if (!matchesSearch) return false;
            }

            // Areas of Interest filter (includes subcategories)
            if (selectedAreasOfInterest.length > 0) {
                const companyAreas = extractAllFilterTags(getFieldValue(company, ['areas of interest', 'focus area', 'cause', 'primary areas']), knownOptions.areas);
                const hasMatchingArea = selectedAreasOfInterest.some(filter => companyAreas.includes(filter));
                if (!hasMatchingArea) return false;
            }

            // Geographic Focus filter (Question 84 - regions like United States, Global, Africa, etc.)
            if (selectedGeographicFocus.length > 0) {
                const geoFocusValue = getFieldValueByKey(company, 'geographic_focus', 84);
                // Split comma-separated values into individual tags
                const companyGeoFocus = geoFocusValue ? extractTags(geoFocusValue, null, true, false) : [];
                const hasMatchingGeoFocus = selectedGeographicFocus.some(filter => companyGeoFocus.includes(filter));
                if (!hasMatchingGeoFocus) return false;
            }

            // Service Locations filter (Question 67 - US States/Countries)
            if (selectedServiceLocations.length > 0) {
                const companyServiceLoc = extractTags(getFieldValueByKey(company, 'service_locations', 67), knownOptions.geo, true);
                const hasMatchingServiceLoc = selectedServiceLocations.some(filter => companyServiceLoc.includes(filter));
                if (!hasMatchingServiceLoc) return false;
            }

            return true;
        }).sort((a, b) => {
            // Default sort by company name (alphabetical, trimmed for leading/trailing spaces)
            const nameA = (a.CompanyName || '').trim().toLowerCase();
            const nameB = (b.CompanyName || '').trim().toLowerCase();
            return nameA.localeCompare(nameB);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableData?.companies, searchQuery, selectedAreasOfInterest, selectedGeographicFocus, selectedServiceLocations, knownOptions]);

    // Toggle filter selection
    const toggleFilter = (filterType, value) => {
        if (filterType === 'areas') {
            setSelectedAreasOfInterest(prev =>
                prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
            );
        } else if (filterType === 'geo') {
            setSelectedGeographicFocus(prev =>
                prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
            );
        } else if (filterType === 'serviceLoc') {
            setSelectedServiceLocations(prev =>
                prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
            );
        }
    };

    // Clear all filters
    const clearFilters = () => {
        setSelectedAreasOfInterest([]);
        setSelectedGeographicFocus([]);
        setSelectedServiceLocations([]);
    };

    const activeFilterCount = selectedAreasOfInterest.length + selectedGeographicFocus.length + selectedServiceLocations.length;

    const handleCardClick = (company) => {
        setSelectedCompany(company);
        setDrawerOpen(true);
    };

    const closeDrawer = () => {
        setDrawerOpen(false);
        setTimeout(() => setSelectedCompany(null), 300);
    };

    const renderDrawerValue = (value, question) => {
        if (!value) {
            return <span className="drawer-empty-value">Not provided</span>;
        }

        if (typeof value === 'object' && value !== null && value.type === 'file' && value.url) {
            const isImage = value.url.match(/\.(jpg|jpeg|png|gif|webp)$/i);
            const s3Key = value.s3_key || value.url;

            const handleFileClick = async (e) => {
                e.preventDefault();
                try {
                    const companyName = selectedCompany?.CompanyName || '';
                    const questionLabel = question.short_description || question.question_text || '';
                    const response = await api.post('/event-entity-companies/file-download', null, {
                        params: {
                            s3_key: s3Key,
                            company_name: companyName,
                            question_label: questionLabel
                        }
                    });
                    if (response.data?.presigned_url) {
                        window.open(response.data.presigned_url, '_blank');
                    }
                } catch (err) {
                    console.error('Error fetching file URL:', err);
                    // Fallback to direct URL if endpoint fails
                    window.open(value.url, '_blank');
                }
            };

            return (
                <div className="drawer-file-section">
                    {isImage ? (
                        <div className="drawer-image-preview">
                            <img src={value.url} alt={question.question_text} />
                        </div>
                    ) : null}
                    <button
                        onClick={handleFileClick}
                        className="drawer-file-link"
                    >
                        <Download size={16} />
                        View File
                    </button>
                </div>
            );
        }

        // Check if this is an "Areas of Interest", "Geographic Focus", or "Service Locations" field - render as chips
        const fieldName = (question.short_description || question.question_text || '').toLowerCase();
        const isAreasOfInterest = fieldName.includes('areas of interest') || fieldName.includes('focus area');
        const isGeographicFocus = isQuestion(question, 'geographic_focus', 84);
        const isServiceLocations = isQuestion(question, 'service_locations', 67);

        if (typeof value === 'object' && value !== null && value.type === 'hierarchical' && Array.isArray(value.items)) {
            // Render hierarchical values (like Areas of Interest) as chips
            if (isAreasOfInterest) {
                return (
                    <div className="nonprofit-card-tags">
                        {value.items.map((item, idx) => (
                            <span key={idx} className="nonprofit-tag">
                                {item.category}
                                {item.subcategories && item.subcategories.length > 0 && (
                                    <span className="nonprofit-tag-subs">
                                        {item.subcategories.map((sub, subIdx) => (
                                            <span key={subIdx}> [{sub}]{subIdx < item.subcategories.length - 1 ? ',' : ''}</span>
                                        ))}
                                    </span>
                                )}
                            </span>
                        ))}
                    </div>
                );
            }

            return (
                <div className="drawer-hierarchical-list">
                    {value.items.map((item, idx) => (
                        <div key={idx} className="drawer-category-group">
                            <div className="drawer-category-main">{item.category}</div>
                            {item.subcategories && item.subcategories.length > 0 && (
                                <>
                                    {item.subcategories.map((sub, subIdx) => (
                                        <div key={subIdx} className="drawer-category-sub">
                                            {sub}
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                    ))}
                </div>
            );
        }

        // Render Geographic Focus as chips (usually comma-separated or simple text)
        if (isGeographicFocus && typeof value === 'string') {
            const tags = value.split(',').map(t => t.trim()).filter(t => t);
            if (tags.length > 0) {
                return (
                    <div className="nonprofit-card-tags">
                        {tags.map((tag, idx) => (
                            <span key={idx} className="nonprofit-tag">{tag}</span>
                        ))}
                    </div>
                );
            }
        }

        // Render Service Locations (Question 67) as chips
        if (isServiceLocations) {
            const tags = extractTags(value, knownOptions.geo, true, false);
            if (tags.length > 0) {
                return (
                    <div className="nonprofit-card-tags">
                        {tags.map((tag, idx) => (
                            <span key={idx} className="nonprofit-tag">{tag}</span>
                        ))}
                    </div>
                );
            }
        }

        const textValue = String(value);

        // Check if it's a URL and make it clickable
        const isUrl = textValue.match(/^(https?:\/\/|www\.)/i) ||
            (textValue.includes('.') && !textValue.includes(' ') &&
                (question.question_text?.toLowerCase().includes('website') ||
                    question.short_description?.toLowerCase().includes('website')));

        if (isUrl) {
            const href = textValue.startsWith('http') ? textValue : `https://${textValue}`;
            return (
                <div className="drawer-text-value">
                    <a href={href} target="_blank" rel="noopener noreferrer" className="drawer-link">
                        {textValue}
                    </a>
                </div>
            );
        }

        return (
            <div className="drawer-text-value">
                {textValue.split('\n').map((line, i) => (
                    <React.Fragment key={i}>
                        {line}
                        {i < textValue.split('\n').length - 1 && <br />}
                    </React.Fragment>
                ))}
            </div>
        );
    };

    if (loading) {
        return (
            <>
                <NavigationBar />
                <div className="event-layout">
                    <EventNavigation eventNameId={eventNameId} />
                    <div className="event-home-container with-sidebar">
                        <LoadingSpinner message="Loading nonprofits..." />
                    </div>
                </div>
            </>
        );
    }

    if (error || !event) {
        return (
            <>
                <NavigationBar />
                <div className="event-layout">
                    <EventNavigation eventNameId={eventNameId} />
                    <div className="event-home-container with-sidebar">
                        <div className="error-message">
                            <h2>Error</h2>
                            <p>{error || 'Event not found'}</p>
                            <button onClick={() => navigate(`/event/${eventNameId}/home`)} className="btn btn-primary">
                                Return to Event Home
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // Handle opening meeting details drawer
    const handleMeetingClick = (e, meeting, companyName) => {
        e.stopPropagation(); // Prevent card click
        setSelectedMeetingId(meeting.meetingId);
        setSelectedMeetingCompanyName(companyName);
        setMeetingDrawerOpen(true);
    };

    // Handle meeting drawer close
    const handleMeetingDrawerClose = () => {
        setMeetingDrawerOpen(false);
        setSelectedMeetingId(null);
        setSelectedMeetingCompanyName(null);
    };

    // Handle meeting action completed (refresh meetings list)
    const handleMeetingAction = () => {
        refreshMeetings();
    };

    // Handle opening meeting request drawer
    const handleMeetRequest = (company) => {
        // Get the first contact with ic_contact_id for the meeting request
        const contactWithIcId = company.contacts?.find(c => c.ic_contact_id);

        if (contactWithIcId) {
            setMeetingRequestContact({
                contact_id: contactWithIcId.ContactId,
                first_name: contactWithIcId.FirstName,
                last_name: contactWithIcId.LastName,
                job_title: contactWithIcId.JobTitle,
                company_name: company.CompanyName,
                image_url: contactWithIcId.ImageUrl,
                ic_contact_id: contactWithIcId.ic_contact_id
            });
            setMeetingRequestDrawerOpen(true);
        } else {
            // No contact with ic_contact_id - show an alert or handle gracefully
            console.warn('No contact with iConnections ID found for meeting request');
        }
    };

    // Handle meeting request drawer close
    const handleMeetingRequestClose = () => {
        setMeetingRequestDrawerOpen(false);
        setMeetingRequestContact(null);
    };

    // Handle meeting request success
    const handleMeetingRequestSuccess = () => {
        setMeetingRequestDrawerOpen(false);
        setMeetingRequestContact(null);
        refreshMeetings();
    };

    // Render meeting status badge/button for a company card
    const renderMeetingStatus = (company) => {
        // Use ic_event_company_id (iConnections ID) for matching against meetings
        const icEventCompanyId = company.ic_event_company_id ? parseInt(company.ic_event_company_id, 10) : null;
        const meetingInfo = getMeetingStatusForCompany(icEventCompanyId);

        if (!meetingInfo) {
            return null; // No meeting with this company
        }

        const { status, meeting } = meetingInfo;

        if (status === 'confirmed') {
            return (
                <button
                    className="card-meeting-badge confirmed"
                    onClick={(e) => handleMeetingClick(e, meeting, company.CompanyName)}
                >
                    <CheckCircle size={14} />
                    <span>Meeting Confirmed</span>
                </button>
            );
        }

        if (status === 'pending_sent') {
            return (
                <button
                    className="card-meeting-badge pending-sent"
                    onClick={(e) => handleMeetingClick(e, meeting, company.CompanyName)}
                >
                    <Clock size={14} />
                    <span>Meeting Pending</span>
                </button>
            );
        }

        if (status === 'pending_received') {
            return (
                <button
                    className="card-meeting-badge pending-received"
                    onClick={(e) => handleMeetingClick(e, meeting, company.CompanyName)}
                >
                    <Clock size={14} />
                    <span>Meeting Action Required</span>
                </button>
            );
        }

        if (status === 'cancelled') {
            return (
                <button
                    className="card-meeting-badge cancelled"
                    onClick={(e) => handleMeetingClick(e, meeting, company.CompanyName)}
                >
                    <X size={14} />
                    <span>Meeting Cancelled</span>
                </button>
            );
        }

        return null;
    };

    // Render a nonprofit card
    const renderNonprofitCard = (company) => {
        // Extract relevant fields - these keywords should match question text/descriptions
        const headquarters = getFieldValue(company, ['headquarters', 'location', 'city', 'address', 'based']);
        const description = getFieldValueByKey(company, 'mission') || getFieldValue(company, ['description', 'mission', 'about', 'overview']);
        const areasOfInterestStructured = extractTags(getFieldValue(company, ['areas of interest', 'focus area', 'cause', 'primary areas']), knownOptions.areas, false, true);
        
        // Question 84 - Geographic Focus (regions like United States, Global, Africa, etc.)
        const geographicFocusValue = getFieldValueByKey(company, 'geographic_focus', 84);
        // Split comma-separated values into individual tags
        const geographicFocus = geographicFocusValue ? extractTags(geographicFocusValue, null, true, false) : [];
        
        // Question 67 - Service Locations (US States/Countries)
        const serviceLocations = extractTags(getFieldValueByKey(company, 'service_locations', 67), knownOptions.geo, true);
        
        const charityNavigatorRating = getFieldValueByKey(company, 'charity_navigator_rating', 65);

        return (
            <div
                key={company.EventCompanyId}
                className="nonprofit-card"
                onClick={() => handleCardClick(company)}
            >
                {charityNavigatorRating &&
                    !['', 'unrated', 'n/a', 'na'].includes(String(charityNavigatorRating).toLowerCase().trim()) && (
                        <div className="nonprofit-rating-badge" data-tooltip="Charity Navigator Rating">
                            <Star size={16} fill="#F59E0B" stroke="#F59E0B" />
                            <span>{charityNavigatorRating}</span>
                        </div>
                    )}
                <div className="nonprofit-card-header no-icon">
                    <div className="nonprofit-card-title-section">
                        <h3 className="nonprofit-card-name">{company.CompanyName}</h3>
                        {headquarters && (
                            <div className="nonprofit-card-location">
                                <MapPin size={14} />
                                <span>{headquarters}</span>
                            </div>
                        )}
                    </div>
                </div>

                {description && (
                    <p className="nonprofit-card-description">{description}</p>
                )}

                {areasOfInterestStructured.length > 0 && (
                    <div className="nonprofit-card-section">
                        <label className="nonprofit-card-label">AREAS OF INTEREST</label>
                        <div className="nonprofit-card-tags">
                            {areasOfInterestStructured.map((item, idx) => (
                                <span key={idx} className="nonprofit-tag">
                                    {item.category}
                                    {item.subcategories && item.subcategories.length > 0 && (
                                        <span className="nonprofit-tag-subs">
                                            {item.subcategories.map((sub, subIdx) => (
                                                <span key={subIdx}> [{sub}]{subIdx < item.subcategories.length - 1 ? ',' : ''}</span>
                                            ))}
                                        </span>
                                    )}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {geographicFocus.length > 0 && (
                    <div className="nonprofit-card-section">
                        <label className="nonprofit-card-label">GEOGRAPHIC FOCUS</label>
                        <div className="nonprofit-card-tags">
                            {geographicFocus.map((tag, idx) => (
                                <span key={idx} className="nonprofit-tag">{tag}</span>
                            ))}
                        </div>
                    </div>
                )}

                {serviceLocations.length > 0 && (
                    <div className="nonprofit-card-section">
                        <label className="nonprofit-card-label">SERVICE LOCATIONS</label>
                        <div className="nonprofit-card-tags">
                            {serviceLocations.map((tag, idx) => (
                                <span key={idx} className="nonprofit-tag">{tag}</span>
                            ))}
                        </div>
                    </div>
                )}

                {company.contacts && company.contacts.length > 0 && (
                    <div className="nonprofit-card-contacts">
                        <label className="nonprofit-card-label">ORGANIZATION CONTACTS</label>
                        <div className="nonprofit-contacts-grid">
                            {company.contacts.map((contact) => (
                                <div key={contact.ContactId} className="nonprofit-contact-item">
                                    {contact.ImageUrl ? (
                                        <img
                                            src={contact.ImageUrl}
                                            alt={`${contact.FirstName} ${contact.LastName}`}
                                            className="nonprofit-contact-avatar"
                                        />
                                    ) : (
                                        <div className="nonprofit-contact-avatar-placeholder">
                                            {contact.FirstName?.[0]}{contact.LastName?.[0]}
                                        </div>
                                    )}
                                    <div className="nonprofit-contact-info">
                                        <div className="nonprofit-contact-name">
                                            {contact.FirstName} {contact.LastName}
                                        </div>
                                        {contact.JobTitle && (
                                            <div className="nonprofit-contact-title">
                                                {contact.JobTitle}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Meeting Status Section */}
                {renderMeetingStatus(company) && (
                    <div className="nonprofit-card-meeting-section">
                        {renderMeetingStatus(company)}
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <NavigationBar />
            <div className="event-layout">
                <EventNavigation eventNameId={eventNameId} />
                <div className="event-home-container with-sidebar event-entity-companies-page nonprofit-cards-page">
                    <div className="event-content">
                        <div className="page-header nonprofit-page-header">
                            <div className="nonprofit-header-content">
                                <h1>Non-profits ({filteredCompanies.length})</h1>
                                <p className="nonprofit-subtitle">
                                    Discover non-profit organizations participating in {event?.EventName || 'Funds for Charity 2026'}
                                </p>
                            </div>
                        </div>

                        <div className="nonprofit-search-bar">
                            <div className="nonprofit-search-input-wrapper">
                                <Search size={18} className="nonprofit-search-icon" />
                                <input
                                    type="text"
                                    placeholder="Search by organization name, mission, focus area, or contact..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="nonprofit-search-input"
                                />
                            </div>
                            <div className="nonprofit-filter-wrapper" ref={filterWrapperRef}>
                                <button
                                    className={`nonprofit-filter-btn ${activeFilterCount > 0 ? 'has-filters' : ''}`}
                                    onClick={() => {
                                        setFilterDropdownOpen(!filterDropdownOpen);
                                        if (!filterDropdownOpen) setFilterView('main');
                                    }}
                                >
                                    <Filter size={16} />
                                    {activeFilterCount > 0 ? `Filters (${activeFilterCount})` : 'Add filter'}
                                    <ChevronDown size={16} className={filterDropdownOpen ? 'rotated' : ''} />
                                </button>

                                {filterDropdownOpen && (
                                    <div className="nonprofit-filter-dropdown">
                                        {/* Main menu - select filter type */}
                                        {filterView === 'main' && (
                                            <>
                                                <div className="filter-menu-item" onClick={() => setFilterView('areas')}>
                                                    <span>Areas of Interest</span>
                                                    <div className="filter-menu-item-right">
                                                        {selectedAreasOfInterest.length > 0 && (
                                                            <span className="filter-count-badge">{selectedAreasOfInterest.length}</span>
                                                        )}
                                                        <ChevronRight size={16} />
                                                    </div>
                                                </div>
                                                <div className="filter-menu-item" onClick={() => setFilterView('geo')}>
                                                    <span>Geographic Focus</span>
                                                    <div className="filter-menu-item-right">
                                                        {selectedGeographicFocus.length > 0 && (
                                                            <span className="filter-count-badge">{selectedGeographicFocus.length}</span>
                                                        )}
                                                        <ChevronRight size={16} />
                                                    </div>
                                                </div>
                                                <div className="filter-menu-item" onClick={() => setFilterView('serviceLoc')}>
                                                    <span>Service Locations</span>
                                                    <div className="filter-menu-item-right">
                                                        {selectedServiceLocations.length > 0 && (
                                                            <span className="filter-count-badge">{selectedServiceLocations.length}</span>
                                                        )}
                                                        <ChevronRight size={16} />
                                                    </div>
                                                </div>
                                                {activeFilterCount > 0 && (
                                                    <div className="filter-actions">
                                                        <button className="filter-clear-btn" onClick={clearFilters}>
                                                            Clear all filters
                                                        </button>
                                                    </div>
                                                )}
                                            </>
                                        )}

                                        {/* Areas of Interest options */}
                                        {filterView === 'areas' && (
                                            <>
                                                <div className="filter-back-header" onClick={() => { setFilterView('main'); setFilterSearchAreas(''); }}>
                                                    <ChevronLeft size={16} />
                                                    <span>Areas of Interest</span>
                                                </div>
                                                <div className="filter-search-wrapper">
                                                    <Search size={14} className="filter-search-icon" />
                                                    <input
                                                        type="text"
                                                        placeholder="Search areas..."
                                                        value={filterSearchAreas}
                                                        onChange={(e) => setFilterSearchAreas(e.target.value)}
                                                        className="filter-search-input"
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </div>
                                                <div className="filter-options">
                                                    {distinctAreasOfInterest
                                                        .filter(area => area.toLowerCase().includes(filterSearchAreas.toLowerCase()))
                                                        .map(area => (
                                                            <label key={area} className="filter-option">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedAreasOfInterest.includes(area)}
                                                                    onChange={() => toggleFilter('areas', area)}
                                                                />
                                                                <span>{area}</span>
                                                            </label>
                                                        ))}
                                                    {distinctAreasOfInterest.filter(area => area.toLowerCase().includes(filterSearchAreas.toLowerCase())).length === 0 && (
                                                        <span className="filter-empty">
                                                            {filterSearchAreas ? 'No matching options' : 'No options available'}
                                                        </span>
                                                    )}
                                                </div>
                                            </>
                                        )}

                                        {/* Geographic Focus options (Question 84) */}
                                        {filterView === 'geo' && (
                                            <>
                                                <div className="filter-back-header" onClick={() => { setFilterView('main'); setFilterSearchGeo(''); }}>
                                                    <ChevronLeft size={16} />
                                                    <span>Geographic Focus</span>
                                                </div>
                                                <div className="filter-search-wrapper">
                                                    <Search size={14} className="filter-search-icon" />
                                                    <input
                                                        type="text"
                                                        placeholder="Search regions..."
                                                        value={filterSearchGeo}
                                                        onChange={(e) => setFilterSearchGeo(e.target.value)}
                                                        className="filter-search-input"
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </div>
                                                <div className="filter-options">
                                                    {distinctGeographicFocus
                                                        .filter(geo => geo.toLowerCase().includes(filterSearchGeo.toLowerCase()))
                                                        .map(geo => (
                                                            <label key={geo} className="filter-option">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedGeographicFocus.includes(geo)}
                                                                    onChange={() => toggleFilter('geo', geo)}
                                                                />
                                                                <span>{geo}</span>
                                                            </label>
                                                        ))}
                                                    {distinctGeographicFocus.filter(geo => geo.toLowerCase().includes(filterSearchGeo.toLowerCase())).length === 0 && (
                                                        <span className="filter-empty">
                                                            {filterSearchGeo ? 'No matching options' : 'No options available'}
                                                        </span>
                                                    )}
                                                </div>
                                            </>
                                        )}

                                        {/* Service Locations options (US States/Countries) */}
                                        {filterView === 'serviceLoc' && (
                                            <>
                                                <div className="filter-back-header" onClick={() => { setFilterView('main'); setFilterSearchServiceLoc(''); }}>
                                                    <ChevronLeft size={16} />
                                                    <span>Service Locations</span>
                                                </div>
                                                <div className="filter-search-wrapper">
                                                    <Search size={14} className="filter-search-icon" />
                                                    <input
                                                        type="text"
                                                        placeholder="Search locations..."
                                                        value={filterSearchServiceLoc}
                                                        onChange={(e) => setFilterSearchServiceLoc(e.target.value)}
                                                        className="filter-search-input"
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </div>
                                                {distinctServiceLocations.length > 0 && (
                                                    <div className="filter-select-all">
                                                        <button
                                                            className="filter-select-all-btn"
                                                            onClick={() => {
                                                                if (selectedServiceLocations.length === distinctServiceLocations.length) {
                                                                    setSelectedServiceLocations([]);
                                                                } else {
                                                                    setSelectedServiceLocations([...distinctServiceLocations]);
                                                                }
                                                            }}
                                                        >
                                                            {selectedServiceLocations.length === distinctServiceLocations.length ? 'Clear All' : 'Select All'}
                                                        </button>
                                                    </div>
                                                )}
                                                <div className="filter-options">
                                                    {distinctServiceLocations
                                                        .filter(loc => loc.toLowerCase().includes(filterSearchServiceLoc.toLowerCase()))
                                                        .map(loc => (
                                                            <label key={loc} className="filter-option">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedServiceLocations.includes(loc)}
                                                                    onChange={() => toggleFilter('serviceLoc', loc)}
                                                                />
                                                                <span>{loc}</span>
                                                            </label>
                                                        ))}
                                                    {distinctServiceLocations.filter(loc => loc.toLowerCase().includes(filterSearchServiceLoc.toLowerCase())).length === 0 && (
                                                        <span className="filter-empty">
                                                            {filterSearchServiceLoc ? 'No matching options' : 'No options available'}
                                                        </span>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Active filter tags */}
                        {activeFilterCount > 0 && (
                            <div className="nonprofit-active-filters">
                                {selectedAreasOfInterest.map(area => (
                                    <span key={`area-${area}`} className="active-filter-tag">
                                        {area}
                                        <button onClick={() => toggleFilter('areas', area)}>
                                            <X size={14} />
                                        </button>
                                    </span>
                                ))}
                                {selectedGeographicFocus.map(geo => (
                                    <span key={`geo-${geo}`} className="active-filter-tag">
                                        {geo}
                                        <button onClick={() => toggleFilter('geo', geo)}>
                                            <X size={14} />
                                        </button>
                                    </span>
                                ))}
                                {selectedServiceLocations.map(loc => (
                                    <span key={`loc-${loc}`} className="active-filter-tag">
                                        {loc}
                                        <button onClick={() => toggleFilter('serviceLoc', loc)}>
                                            <X size={14} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}

                        {!tableData || tableData.companies.length === 0 ? (
                            <div className="empty-state">
                                <Building2 size={48} />
                                <h3>No Nonprofits Registered</h3>
                                <p>There are no nonprofits registered for this event yet.</p>
                            </div>
                        ) : filteredCompanies.length === 0 ? (
                            <div className="empty-state">
                                <Search size={48} />
                                <h3>No Results Found</h3>
                                <p>No nonprofits match your search criteria. Try adjusting your search.</p>
                            </div>
                        ) : (
                            <div className="nonprofit-cards-container">
                                {filteredCompanies.map(company => renderNonprofitCard(company))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Profile Drawer */}
            {drawerOpen && (
                <>
                    <div className="drawer-overlay" onClick={closeDrawer} />
                    <div className={`profile-drawer ${drawerOpen ? 'open' : ''}`}>
                        <div className="drawer-header">
                            <h2>{selectedCompany?.CompanyName}</h2>
                            <button className="drawer-close-btn" onClick={closeDrawer} aria-label="Close drawer">
                                <X size={24} />
                            </button>
                        </div>

                        <div className="drawer-content">
                            {/* Entity Tag */}
                            <span
                                className="drawer-entity-tag"
                                style={tableData?.event_entity_color ? {
                                    '--entity-bg-color': `${tableData.event_entity_color}20`,
                                    '--entity-border-color': `${tableData.event_entity_color}40`,
                                    '--entity-text-color': tableData.event_entity_color
                                } : undefined}
                            >{tableData?.event_entity_name}</span>

                            {/* Organization Contacts */}
                            {selectedCompany?.contacts && selectedCompany.contacts.length > 0 && (
                                <div className="drawer-contacts-section">
                                    {selectedCompany.contacts.map((contact) => (
                                        <div
                                            key={contact.ContactId}
                                            className="drawer-contact-item"
                                            onClick={() => window.open(`/user/${contact.ContactId}`, '_blank')}
                                            title="View profile"
                                        >
                                            {contact.ImageUrl && (
                                                <img
                                                    src={contact.ImageUrl}
                                                    alt={`${contact.FirstName} ${contact.LastName}`}
                                                    className="drawer-contact-avatar"
                                                />
                                            )}
                                            {!contact.ImageUrl && (
                                                <div className="drawer-contact-avatar-placeholder">
                                                    {contact.FirstName?.[0]}{contact.LastName?.[0]}
                                                </div>
                                            )}
                                            <div className="drawer-contact-info">
                                                <div className="drawer-contact-name">
                                                    {contact.FirstName} {contact.LastName}
                                                </div>
                                                {contact.JobTitle && (
                                                    <div className="drawer-contact-title">
                                                        {contact.JobTitle}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {tableData?.questions
                                .filter((question) => {
                                    const value = selectedCompany?.answers[question.question_id];
                                    return value !== null && value !== undefined && value !== '';
                                })
                                .sort((a, b) => {
                                    // Custom sort to put Organization Description first, then Areas of Interest, then Geographic Focus, then Service Locations
                                    const getOrder = (q) => {
                                        const text = (q.short_description || q.question_text || '').toLowerCase();
                                        if (text.includes('organization description') || text.includes('org description') || isQuestion(q, 'mission')) return 1;
                                        if (text.includes('areas of interest') || text.includes('focus area')) return 2;
                                        // Question 84 - Geographic Focus (regions) - comes before Service Locations
                                        if (isQuestion(q, 'geographic_focus', 84)) return 3;
                                        // Question 67 - Service Locations (US States/Countries)
                                        if (isQuestion(q, 'service_locations', 67)) return 4;
                                        return 100; // All other fields come after
                                    };
                                    return getOrder(a) - getOrder(b);
                                })
                                .map((question) => {
                                    const value = selectedCompany?.answers[question.question_id];

                                    // Transform certain labels for better display
                                    let labelText = question.short_description || question.question_text;
                                    if (labelText?.toLowerCase().includes('donor areas of interest')) {
                                        labelText = labelText.replace(/donor\s+/i, '');
                                    }
                                    // Question 84 - rename to Geographic Focus
                                    if (isQuestion(question, 'geographic_focus', 84)) {
                                        labelText = 'Geographic Focus';
                                    }
                                    // Question 67 - rename to Service Locations
                                    if (isQuestion(question, 'service_locations', 67)) {
                                        labelText = 'Service Locations';
                                    }
                                    // Financials - truncate long description
                                    if (labelText?.toLowerCase().startsWith('financials')) {
                                        labelText = 'Financials';
                                    }

                                    return (
                                        <div key={question.question_id} className="drawer-field">
                                            <label className="drawer-field-label">
                                                {labelText}
                                            </label>
                                            <div className="drawer-field-value">
                                                {renderDrawerValue(value, question)}
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>

                        <div className="drawer-footer">
                            {(() => {
                                const icEventCompanyId = selectedCompany?.ic_event_company_id
                                    ? parseInt(selectedCompany.ic_event_company_id, 10)
                                    : null;
                                const meetingInfo = getMeetingStatusForCompany(icEventCompanyId);

                                if (meetingInfo) {
                                    const { status, meeting } = meetingInfo;

                                    if (status === 'confirmed') {
                                        return (
                                            <button
                                                className="drawer-meet-btn meeting-status-btn"
                                                onClick={() => {
                                                    setSelectedMeetingId(meeting.meetingId);
                                                    setSelectedMeetingCompanyName(selectedCompany.CompanyName);
                                                    setMeetingDrawerOpen(true);
                                                }}
                                            >
                                                <CheckCircle size={14} />
                                                Meeting Confirmed
                                            </button>
                                        );
                                    }

                                    if (status === 'pending_sent') {
                                        return (
                                            <button
                                                className="drawer-meet-btn meeting-status-btn"
                                                onClick={() => {
                                                    setSelectedMeetingId(meeting.meetingId);
                                                    setSelectedMeetingCompanyName(selectedCompany.CompanyName);
                                                    setMeetingDrawerOpen(true);
                                                }}
                                            >
                                                <Clock size={14} />
                                                Meeting Pending
                                            </button>
                                        );
                                    }

                                    if (status === 'pending_received') {
                                        return (
                                            <button
                                                className="drawer-meet-btn meeting-status-btn"
                                                onClick={() => {
                                                    setSelectedMeetingId(meeting.meetingId);
                                                    setSelectedMeetingCompanyName(selectedCompany.CompanyName);
                                                    setMeetingDrawerOpen(true);
                                                }}
                                            >
                                                <Clock size={14} />
                                                Meeting Action Required
                                            </button>
                                        );
                                    }

                                    if (status === 'cancelled') {
                                        return (
                                            <button
                                                className="drawer-meet-btn meeting-status-btn"
                                                onClick={() => {
                                                    setSelectedMeetingId(meeting.meetingId);
                                                    setSelectedMeetingCompanyName(selectedCompany.CompanyName);
                                                    setMeetingDrawerOpen(true);
                                                }}
                                            >
                                                <X size={14} />
                                                Meeting Cancelled
                                            </button>
                                        );
                                    }
                                }

                                // Default: No meeting - show Meet button
                                return (
                                    <button
                                        className="drawer-meet-btn"
                                        onClick={() => handleMeetRequest(selectedCompany)}
                                    >
                                        Meet
                                    </button>
                                );
                            })()}
                        </div>
                    </div>
                </>
            )}

            {/* Meeting Details Drawer */}
            <MeetingDetailsDrawer
                meetingId={selectedMeetingId}
                isOpen={meetingDrawerOpen}
                onClose={handleMeetingDrawerClose}
                onMeetingCancelled={handleMeetingAction}
                counterpartyCompanyName={selectedMeetingCompanyName}
            />

            {/* Meeting Request Drawer */}
            <MeetingRequestDrawer
                isOpen={meetingRequestDrawerOpen}
                onClose={handleMeetingRequestClose}
                contact={meetingRequestContact}
                eventId={event?.EventId}
                icEventId={event?.ic_event_id}
                onSuccess={handleMeetingRequestSuccess}
            />
        </>
    );
};

export default EventNonprofits;
